import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import Seo from '../components/Seo';
import '../components/booth/tokens.css';
import BoothMark from '../components/booth/BoothMark';
import BoothField from '../components/booth/BoothField';
import Footer from '../components/booth/Footer';
import './Feedback.css';

// ----- Helpers -----

function getFirstTwoSentences(text) {
  if (!text) return '';
  const matches = text.match(/[^.!?]*[.!?]+(\s|$)/g) || [];
  const preview = matches.slice(0, 2).join('').trim();
  return preview || text;
}

// ----- Default data functions (use real Supabase) -----

async function defaultHandleQuickRating(token, rating) {
  const ratingInt = parseInt(rating, 10);
  if (!token || isNaN(ratingInt) || ratingInt < 1 || ratingInt > 5) {
    return 'invalid';
  }

  const { data: tokenRow, error } = await supabase
    .from('feedback_tokens')
    .select('*')
    .eq('token', token)
    .maybeSingle();

  if (error || !tokenRow) return 'invalid';
  if (tokenRow.used_at) return 'already-submitted';

  const { error: insertError } = await supabase.from('recap_feedback').insert({
    recap_id: tokenRow.recap_id,
    feedback_token: token,
    rating: ratingInt,
    feedback_text: null,
    submitted_at: new Date().toISOString(),
  });
  if (insertError) return 'invalid';

  await supabase
    .from('feedback_tokens')
    .update({ used_at: new Date().toISOString() })
    .eq('token', token);

  return 'thank-you';
}

async function defaultLoadFeedbackData(token) {
  const { data: tokenData, error: tokenError } = await supabase
    .from('feedback_tokens')
    .select('*, recaps(week)')
    .eq('token', token)
    .maybeSingle();

  if (tokenError || !tokenData) return { status: 'invalid' };
  if (tokenData.used_at) return { status: 'already-submitted' };

  const { data: recapList, error: recapError } = await supabase
    .from('recaps')
    .select('*')
    .eq('league_id', tokenData.league_id)
    .eq('week', tokenData.recaps.week)
    .order('matchup_id');

  if (recapError || !recapList) return { status: 'invalid' };
  return { status: 'ready', recaps: recapList };
}

// A global, whole-week comment has nowhere of its own to live — recap_feedback
// rows always belong to one recap_id. Rather than add a column, it's persisted
// as its own row (rating: null marks it as the overall comment, not a
// per-matchup rating) anchored to a rated matchup's recap_id when one exists,
// or the feedback token's own recap_id when nothing was individually rated.
export async function defaultSubmitFeedbackData(token, rows, globalComment) {
  const trimmedComment = (globalComment || '').trim();
  let allRows = rows;

  if (trimmedComment) {
    let anchorRecapId = rows[0]?.recap_id;
    if (!anchorRecapId) {
      const { data: tokenRow } = await supabase
        .from('feedback_tokens')
        .select('recap_id')
        .eq('token', token)
        .maybeSingle();
      anchorRecapId = tokenRow?.recap_id;
    }
    if (anchorRecapId) {
      allRows = [
        ...rows,
        {
          recap_id: anchorRecapId,
          feedback_token: token,
          rating: null,
          feedback_text: trimmedComment,
          submitted_at: new Date().toISOString(),
        },
      ];
    }
  }

  if (allRows.length > 0) {
    const { error } = await supabase.from('recap_feedback').insert(allRows);
    if (error) throw error;
  }

  await supabase
    .from('feedback_tokens')
    .update({ used_at: new Date().toISOString() })
    .eq('token', token);
}

// ----- Sub-components -----

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="booth-feedback-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`booth-feedback-star ${
            n <= (hover || value) ? 'booth-feedback-star--active' : ''
          }`}
          onClick={() => onChange(value === n ? 0 : n)}
          onMouseEnter={() => setHover(n)}
          onMouseLeave={() => setHover(0)}
          role="button"
          aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && onChange(value === n ? 0 : n)}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function StarsDisplay({ rating }) {
  return (
    <div className="booth-feedback-stars booth-feedback-stars--display">
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          className={`booth-feedback-star ${
            n <= rating ? 'booth-feedback-star--active' : ''
          }`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function MatchupCard({ recap, rating, comment, onRatingChange, onCommentChange }) {
  const [expanded, setExpanded] = useState(false);
  const preview = getFirstTwoSentences(recap.writing);
  const hasMore = recap.writing && recap.writing.length > preview.length;

  return (
    <div className="booth-feedback-card">
      <p className="booth-feedback-card-title">
        {recap.home_team_name} vs. {recap.away_team_name}
      </p>
      <p className="booth-feedback-card-score">
        {recap.home_score} – {recap.away_score}
      </p>
      <p className="booth-feedback-card-excerpt">
        {expanded ? recap.writing : preview}
      </p>
      {hasMore && !expanded && (
        <button
          type="button"
          className="booth-link-underline booth-feedback-read-more"
          onClick={() => setExpanded(true)}
        >
          Read more
        </button>
      )}
      <StarRating value={rating} onChange={onRatingChange} />
      <BoothField
        id={`feedback-comment-${recap.id}`}
        label="What could be better?"
        name={`comment-${recap.id}`}
        multiline
        rows={2}
        value={comment}
        onChange={(e) => onCommentChange(e.target.value)}
      />
    </div>
  );
}

// ----- Page states -----

function MessageState({ heading, body }) {
  return (
    <>
      <h1 className="booth-auth-title">{heading}</h1>
      <p className="booth-feedback-dek">{body}</p>
    </>
  );
}

function ThankYouState({ rating }) {
  return (
    <>
      {rating > 0 && <StarsDisplay rating={rating} />}
      <h1 className="booth-auth-title">Thanks for the feedback.</h1>
      <p className="booth-feedback-dek">See you next Tuesday.</p>
    </>
  );
}

// ----- Main component -----

export default function Feedback({
  loadFeedbackData = defaultLoadFeedbackData,
  submitFeedbackData = defaultSubmitFeedbackData,
  handleQuickRating = defaultHandleQuickRating,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [recaps, setRecaps] = useState([]);
  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [globalComment, setGlobalComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submittedRating, setSubmittedRating] = useState(null);

  useEffect(() => {
    const path = location.pathname;
    const search = location.search;
    const params = new URLSearchParams(search);

    if (path === '/feedback/already-submitted') {
      setStatus('already-submitted');
      return;
    }
    if (path === '/feedback/invalid') {
      setStatus('invalid');
      return;
    }
    if (path === '/feedback/thank-you') {
      setSubmittedRating(parseInt(params.get('rating'), 10) || 0);
      setStatus('thank-you');
      return;
    }
    if (path === '/feedback/quick') {
      const token = params.get('token');
      const rating = params.get('rating');
      handleQuickRating(token, rating).then((result) => {
        if (result === 'thank-you') {
          setSubmittedRating(parseInt(rating, 10) || 0);
        }
        setStatus(result);
      });
      return;
    }

    const token = params.get('token');
    if (!token) {
      setStatus('invalid');
      return;
    }

    loadFeedbackData(token).then((result) => {
      if (result.status === 'ready') {
        setRecaps(result.recaps);
      }
      setStatus(result.status);
    });
  }, [loadFeedbackData, handleQuickRating, location]);

  async function handleSubmit() {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');

    setSubmitting(true);

    const ratedRows = recaps
      .filter((r) => ratings[r.id] > 0)
      .map((r) => ({
        recap_id: r.id,
        feedback_token: token,
        rating: ratings[r.id],
        feedback_text: comments[r.id] || null,
        submitted_at: new Date().toISOString(),
      }));

    try {
      await submitFeedbackData(token, ratedRows, globalComment.trim());
      const highestRating = Math.max(0, ...Object.values(ratings));
      setSubmittedRating(highestRating);
      setStatus('submitted');
    } catch (err) {
      console.error('Error submitting feedback:', err);
    } finally {
      setSubmitting(false);
    }
  }

  function renderContent() {
    switch (status) {
      case 'loading':
        return (
          <div className="booth-auth-card">
            <p className="booth-feedback-dek">Loading…</p>
          </div>
        );
      case 'invalid':
        return (
          <div className="booth-auth-card">
            <MessageState
              heading="Link expired"
              body="This feedback link is no longer valid."
            />
          </div>
        );
      case 'already-submitted':
        return (
          <div className="booth-auth-card">
            <MessageState
              heading="Already submitted"
              body="Looks like you already rated this week's recaps."
            />
          </div>
        );
      case 'thank-you':
      case 'submitted':
        return (
          <div className="booth-auth-card booth-feedback-thanks">
            <ThankYouState rating={submittedRating} />
          </div>
        );
      case 'ready':
        return (
          <div className="booth-feedback-ready">
            <div className="booth-feedback-week-header">
              <p className="booth-auth-eyebrow">Weekly recap</p>
              <h1 className="booth-auth-title">How did we do?</h1>
            </div>
            {recaps.map((recap) => (
              <MatchupCard
                key={recap.id}
                recap={recap}
                rating={ratings[recap.id] || 0}
                comment={comments[recap.id] || ''}
                onRatingChange={(val) =>
                  setRatings((prev) => ({ ...prev, [recap.id]: val }))
                }
                onCommentChange={(val) =>
                  setComments((prev) => ({ ...prev, [recap.id]: val }))
                }
              />
            ))}
            <div className="booth-feedback-global">
              <BoothField
                id="feedback-global-comment"
                label="Anything else?"
                name="globalComment"
                multiline
                rows={3}
                value={globalComment}
                onChange={(e) => setGlobalComment(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="booth-btn booth-btn--primary booth-auth-submit"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Sending…' : 'Send feedback'}
            </button>
          </div>
        );
      default:
        return null;
    }
  }

  return (
    <div className="booth booth-feedback-page">
      <Seo
        title="Weekly feedback — The Booth"
        description="Rate this week's dispatch."
        path="/feedback"
        noindex
      />
      <header className="booth-shell booth-feedback-masthead">
        <a
          href="/"
          className="booth-feedback-mark-link"
          onClick={(e) => {
            e.preventDefault();
            navigate('/');
          }}
        >
          <BoothMark />
        </a>
      </header>
      <main className="booth-shell booth-feedback-main">{renderContent()}</main>
      <Footer />
    </div>
  );
}
